import { FormGroup } from '@angular/forms';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

import {
  CreateEntityConnectionGQL,
  CreateEntityConnectionInput,
  CreateNoteGQL, CreateNotesInput, CustomFormDetailsFragment, EntityCore, NoteTarget
} from 'generated/graphql';

import { ApplyTagsService, OnTagAppliedResponse } from 'field-shared/apply-tags.service';
import { isNilOrEmpty } from 'helpers';

import { CustomFormApplierProgress } from './CustomFormApplierProgress';
import { CustomFormSubmissionService } from './CustomFormSubmission.service';
import { FullNamePipe } from 'shared/pipes/full-name.pipe';

/**
 * This class is responsible for applying a custom form to an existing entity.
 * It will apply tags and notes to the entity.
 * It will emit progress events as it applies the form items.
 * You can subscribe to the progress$ observable to get these events.
 *
 */

export class CustomFormApplier {

  private itemsCreated = 0;

  private progressSubject = new Subject<CustomFormApplierProgress>();

  public progress$ = this.progressSubject.asObservable();

  constructor(
    public entity: EntityCore.Fragment,
    public form: FormGroup,
    public customFormSettings: CustomFormDetailsFragment,
    private _applyTagsService: ApplyTagsService,
    private _createNotesGQL: CreateNoteGQL,
    private _createEntityConnectionGQL: CreateEntityConnectionGQL
  ) { }

  /**
   * Runs mutations to apply the form to the entity.
   * Emits progress events as it runs. You can subscribe to the progress$ observable to get these events.
   * @returns Observable<EntityCore.Fragment> - The entity that was passed in.
   */
  public applyForm(): Observable<EntityCore.Fragment> {
    const entity = this.entity;

    const tags$ = this._applyTagsToEntity();
    const notes$ = this._applyNotesToEntity();
    const connections$ = this._applyConnectionToEntity();

    return tags$.pipe(
      switchMap(() => notes$),
      switchMap(() => connections$),
      map(() => entity),
      finalize(() => this.progressSubject.complete())
    );
  };


  private _applyNotesToEntity(): Observable<EntityCore.Fragment> {
    const entity = this.entity;
    const notes = CustomFormSubmissionService.GetNotesFromForm(this.form);
    if (isNilOrEmpty(notes)) {
      return of(entity);
    }

    const input: CreateNotesInput = {
      campaignId: entity.campaignContextId,
      targetId: entity.id,
      targetType: NoteTarget.Entity,
      notes
    };

    this.updateProgress({ statusMessage: `Adding Note...` });

    return this._createNotesGQL.mutate({ input }).pipe(
      finalize(() => this.updateProgress({ statusMessage: `Note Added`, shouldIncrement: true })),
      catchError((err) => {
        this.updateProgress({ statusMessage: `Error Adding Note`, shouldIncrement: true, errors: [err] });
        return of(entity);
      }),
      map(() => entity)
    );
  }

  private _applyConnectionToEntity() {
    const entity = this.entity;
    const connections = CustomFormSubmissionService.GetConnectionsFromForm(this.form);
    if (isNilOrEmpty(connections)) {
      return of(entity);
    }

    const fnPipe = new FullNamePipe();

    const connectionObservables = connections.map((connection) => {
      const connectionName = fnPipe.transform(connection?.selectedEntity);
      const mutationInput: CreateEntityConnectionInput = {
        entityConnectionTypeId: connection.entityConnectionType.id,
        toEntityId: connection.selectedEntity.id,
        fromEntityId: entity.id,
        note: connection.note,
        campaignId: entity.campaignContextId
      }

      this.updateProgress({ statusMessage: `Creating Connection To ${connectionName}` });
      return this._createEntityConnectionGQL.mutate({ input: mutationInput }).pipe(
        finalize(() => this.updateProgress({ statusMessage: `Connection Added `, shouldIncrement: true })),
        catchError((err) => {
          this.updateProgress({ statusMessage: `Error Adding Connection`, shouldIncrement: true, errors: [err] });
          return of(entity);
        }),
        map(() => entity)
      );
    });

    if (connectionObservables.length === 0) {
      return of(entity);
    }

    return concat(...connectionObservables).pipe(
      map(() => entity)
    );
  }

  private _applyTagsToEntity() {
    const selectedTags = CustomFormSubmissionService.GetTagsFromForm(this.form).map(c => c.selectedTag);
    if (isNilOrEmpty(selectedTags)) {
      return of(this.entity);
    }

    const getTagName = (tagId: string) => selectedTags.find(s => s.id === tagId)?.name;
    const onAppliedCallback = (onAppliedResponse: OnTagAppliedResponse) => {
      const tagId = onAppliedResponse.mutationInput.tagId;
      if (onAppliedResponse['error']) {
        this.updateProgress(
          {
            statusMessage: `Error Adding Response: ${getTagName(tagId)}`,
            shouldIncrement: true,
            errors: [onAppliedResponse['error']]
          });
      } else {
        this.updateProgress(
          {
            statusMessage: `Added Response: ${getTagName(tagId)}`,
            shouldIncrement: true
          });
      }
    };

    return this._applyTagsService.applyTags({
      targetInfo: {
        targetId: this.entity.id,
        targetType: 'Entity',
        campaignId: this.entity.campaignContextId
      },
      tags: CustomFormSubmissionService.GetTagsFromForm(this.form)
    }, onAppliedCallback).pipe(
      tap(
        (result) => {
          if (result.errors.length > 0) {
            this.updateProgress(
              {
                statusMessage: 'Entity Created, but there were some errors adding responses.',
                shouldIncrement: false,
                errors: result.errors,
                toastLevel: 'warning'
              });
          }
        }
      ),
      map(() => this.entity)
    );
  }

  private updateProgress(
    { statusMessage, shouldIncrement = false, errors = [], toastLevel = null }: {
      statusMessage: string;
      shouldIncrement?: boolean; errors?: any[]; toastLevel?: any;
    }) {
    if (shouldIncrement) {
      this.itemsCreated++;
    }
    const itemsToCreate = CustomFormSubmissionService.NumberOfItemsToCreate(this.form, this.customFormSettings);
    this.progressSubject.next({
      statusMessage,
      entity: this.entity,
      errors,
      toastLevel,
      itemsToCreate,
      itemsCreated: this.itemsCreated,
      createProgress: (this.itemsCreated / itemsToCreate) * 100
    });
  }
}
